import { useEffect, useState } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Slider,
    Button,
    Box,
    useTheme,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import { request } from "@/Api";
import { AccountSectionTitle } from "@/account/StyledComponents";
import Payment from "@/payment/Payment";
import { formattedCurrency, formattedNumber } from "@/utils";
import { useSelector } from "react-redux";
import { selectPersonId } from "@/auth/authSlice";

export default function AiCreditSection({ data }) {
    // Example state for slider and price
    const theme = useTheme();
    const [creditAmount, setCreditAmount] = useState(2000);
    const [paying, setPaying] = useState(false);
    const [person, setPerson] = useState(null);

    const personId = useSelector(selectPersonId);
    const getPerson = () => {
        request.get(`/persons/${personId}`).then((response) => {
            setPerson(response.data);
        });
    };

    const pricePerCredit = 0.01;
    const minCredits = 2000;
    const maxCredits = 20000;
    const step = 1000;
    const priceMoney = creditAmount * pricePerCredit;
    const price = priceMoney.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    });

    // Example static data
    const totalSpent = formattedNumber(data.credit_total_spent || 0);
    const remainingCredits = formattedNumber(data.credit_balance || 0);
    const inputUsage = person?.total_usage.input_tokens || 0;
    const outputUsage = person?.total_usage.output_tokens || 0;
    const totalUsage = formattedNumber(inputUsage + outputUsage);

    const cardStyle = {
        borderRadius: "16px",
        border: "1px solid #363636",
        background: theme.palette.background.default,
        minWidth: 300,
    };

    useEffect(() => {
        getPerson();
    }, []);

    return (
        <Grid item>
            <AccountSectionTitle>AI Credits</AccountSectionTitle>
            <Grid container spacing={3} sx={{ mt: 2, alignItems: "stretch" }}>
                {/* Left Card: Remaining Credits & Usage */}
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            ...cardStyle,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <CardContent
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ opacity: 0.7 }}
                                >
                                    Remaining Credits:
                                </Typography>
                                <Typography variant="body2">
                                    {remainingCredits}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ opacity: 0.7, mt: 3 }}
                                >
                                    Lifetime Usage
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mt: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ opacity: 0.7 }}
                                    >
                                        Input Tokens:
                                    </Typography>
                                    <Typography variant="body2">
                                        {formattedNumber(inputUsage)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ opacity: 0.7 }}
                                    >
                                        Output Tokens:
                                    </Typography>
                                    <Typography variant="body2">
                                        {formattedNumber(outputUsage)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mt: 1,
                                        borderTop: "1px solid #333",
                                        pt: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ opacity: 0.7 }}
                                    >
                                        Total Credits:
                                    </Typography>
                                    <Typography variant="body2">
                                        {totalSpent}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Right Card: Add Credits */}
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            ...cardStyle,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <CardContent
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ opacity: 0.7 }}
                            >
                                Add credits
                            </Typography>
                            <Typography variant="h4" sx={{ mt: 1, mb: 2 }}>
                                {price}
                            </Typography>
                            <Box>
                                <Box sx={{ padding: 1 }}>
                                    <Slider
                                        value={creditAmount}
                                        min={minCredits}
                                        max={maxCredits}
                                        step={step}
                                        onChange={(_, val) =>
                                            setCreditAmount(val)
                                        }
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    {creditAmount.toLocaleString()} Credits
                                </Typography>
                                <Chip
                                    onClick={() => setPaying(true)}
                                    clickable
                                    label={"Purchase"}
                                    variant="contained"
                                    color="secondary"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Dialog
                open={paying}
                onClose={() => setPaying(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Purchase AI Credits</DialogTitle>
                <DialogContent>
                    <Payment
                        confirmText="Purchase"
                        priceKey="ai_credits"
                        amount={priceMoney}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    );
}
